import { Box, Grid, Typography, CircularProgress, Paper, Avatar, Chip, LinearProgress, IconButton, Tooltip, Card, CardContent } from '@mui/material'
import React, { useEffect, useState } from 'react'
import TextLabel from '../Components/Common/CommonTextLabel'
import ButtonContained from '../Components/Common/ButtonContained'
import { makeStyles } from 'tss-react/mui'
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import { useNavigate } from 'react-router'
import { useDispatch } from 'react-redux'
import { showLoader } from '../Redux/Slices/loaderSlice'
import { getAvailableAccess } from '../Redux/Actions/action'
import { ApiGet } from '../ApiSetup/Api'
import { api } from '../ApiSetup/Axios'
import { toast } from 'react-toastify'
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer,
    PieChart, Pie, Cell, BarChart, Bar, Legend, LineChart, Line
} from 'recharts'

const CHART_COLORS = ['#3E4094', '#5B6BC0', '#7986CB', '#9FA8DA', '#C5CAE9'];

const useStyles = makeStyles()((theme) => ({
    headerCard: {
        borderRadius: "16px",
        padding: "28px 32px",
        background: "#3E4094",
        [theme.breakpoints.down('sm')]: {
            padding: "20px",
        },
    },
    actionButtons: {
        display: "flex",
        flexWrap: "wrap",
        gap: "10px",
        marginTop: "16px",
        [theme.breakpoints.up('md')]: {
            marginTop: "0px",
            justifyContent: "flex-end",
        },
    },
    statCard: {
        padding: "20px",
        borderRadius: "12px",
        height: "100%",
        transition: "box-shadow 0.2s",
        cursor: "pointer",
        border: "1px solid #e5e7eb",
        background: "#fff",
        "&:hover": {
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
        }
    },
    iconBox: {
        width: 48,
        height: 48,
        borderRadius: "10px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    chartCard: {
        borderRadius: "12px",
        padding: "20px",
        background: "#fff",
        border: "1px solid #e5e7eb",
        height: "100%",
    },
    sectionTitle: {
        fontSize: "15px",
        fontWeight: 600,
        color: "#374151",
        marginBottom: "16px",
    },
    miniStatCard: {
        padding: "14px 16px",
        borderRadius: "10px",
        background: "#f9fafb",
        border: "1px solid #e5e7eb",
    },
    transactionItem: {
        padding: "10px 12px",
        borderRadius: "8px",
        background: "#f9fafb",
        marginBottom: "6px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },
    refreshButton: {
        background: "rgba(255,255,255,0.15)",
        color: "#fff",
        "&:hover": {
            background: "rgba(255,255,255,0.25)",
        }
    }
}))

const Dashboard = () => {
    const { classes } = useStyles();
    const navigate = useNavigate()
    const dispatch = useDispatch();

    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [dashboardData, setDashboardData] = useState({
        members: {
            total: 0, today: 0, thisWeek: 0, thisMonth: 0,
            pending: 0, approved: 0, rejected: 0, draft: 0,
            loginUsers: 0, partialRejected: 0, reUploaded: 0, paidMembers: 0
        },
        transactions: {
            total: 0, totalAmount: 0,
            today: { count: 0, amount: 0 },
            thisWeek: { count: 0, amount: 0 },
            thisMonth: { count: 0, amount: 0 },
            pending: 0, success: 0, failed: 0,
            byPaymentMode: []
        },
        memberTrend: [],
        transactionTrend: [],
        verificationDistribution: [],
        recentTransactions: [],
        tickets: { total: 0, open: 0, inProgress: 0, resolved: 0, closed: 0 },
        claims: { total: 0, pending: 0, approved: 0, rejected: 0, totalAmount: 0 },
        ageDistribution: [],
        topReferrers: []
    });

    const fetchDashboardData = async (isRefresh = false) => {
        try {
            if (isRefresh) setRefreshing(true);
            else setLoading(true);
            
            const response = await ApiGet(api.getDashboardStats);
            if (response?.data?.status === 200) {
                setDashboardData(response?.data?.data);
            }
        } catch (error) {
            console.error("Dashboard fetch error:", error);
            if (!error?.cancelled) {
                toast.error("Failed to load dashboard data");
            }
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        dispatch(showLoader(true));
        dispatch(getAvailableAccess())
            .unwrap()
            .catch((error) => console.error("error", error))
            .finally(() => dispatch(showLoader(false)));
        
        fetchDashboardData();
    }, [dispatch]);

    const formatCurrency = (num) => {
        if (!num) return '₹0';
        if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)} Cr`;
        if (num >= 100000) return `₹${(num / 100000).toFixed(2)} L`;
        if (num >= 1000) return `₹${(num / 1000).toFixed(1)}K`;
        return `₹${num.toLocaleString('en-IN')}`;
    };

    const formatNumber = (num) => {
        if (!num) return '0';
        return num.toLocaleString('en-IN');
    };

    const getPaymentModeName = (type) => {
        const modes = { 1: 'Cheque', 2: 'RTGS', 3: 'QR', 4: 'UPI', 5: 'NEFT', 6: 'IMPS', 7: 'Other' };
        return modes[type] || 'Other';
    };

    const statsCards = [
        {
            title: "Total Members",
            count: dashboardData.members?.total,
            subText: `+${dashboardData.members?.today || 0} today`,
            icon: <PeopleAltOutlinedIcon sx={{ color: "#3E4094", fontSize: 24 }} />,
            iconBg: "#EEF0FF",
            onClick: () => navigate('/admin/memberSearch')
        },
        {
            title: "Approved",
            count: dashboardData.members?.approved,
            subText: `${dashboardData.members?.paidMembers || 0} paid`,
            icon: <VerifiedUserOutlinedIcon sx={{ color: "#059669", fontSize: 24 }} />,
            iconBg: "#ECFDF5",
            onClick: () => navigate('/admin/documents-view-status', { state: { status: 'Approved' } })
        },
        {
            title: "Pending",
            count: dashboardData.members?.pending,
            subText: `${dashboardData.members?.draft || 0} drafts`,
            icon: <PendingActionsOutlinedIcon sx={{ color: "#D97706", fontSize: 24 }} />,
            iconBg: "#FEF3C7",
            onClick: () => navigate('/admin/documents')
        },
        {
            title: "Collection",
            count: formatCurrency(dashboardData.transactions?.totalAmount),
            subText: `${formatCurrency(dashboardData.transactions?.thisMonth?.amount || 0)} this month`,
            icon: <AccountBalanceWalletOutlinedIcon sx={{ color: "#059669", fontSize: 24 }} />,
            iconBg: "#ECFDF5",
            onClick: () => navigate('/admin/allTransactions'),
            isAmount: true
        },
        {
            title: "Claims",
            count: dashboardData.claims?.pending,
            subText: `${dashboardData.claims?.total || 0} total`,
            icon: <AssignmentTurnedInOutlinedIcon sx={{ color: "#DC2626", fontSize: 24 }} />,
            iconBg: "#FEF2F2",
            onClick: () => navigate('/admin/claims')
        },
        {
            title: "Transactions",
            count: dashboardData.transactions?.success,
            subText: `${dashboardData.transactions?.today?.count || 0} today`,
            icon: <PaymentsOutlinedIcon sx={{ color: "#3E4094", fontSize: 24 }} />,
            iconBg: "#EEF0FF",
            onClick: () => navigate('/admin/allTransactions')
        },
    ];

    const verificationChartData = dashboardData.verificationDistribution?.filter(item => item.count > 0) || [];
    
    const paymentModeData = dashboardData.transactions?.byPaymentMode?.map(item => ({
        name: getPaymentModeName(item._id),
        value: item.count,
        amount: item.amount
    })) || [];

    return (
        <Grid container spacing={2.5}>
            {/* Header Card */}
            <Grid item xs={12}>
                <Box className={classes.headerCard}>
                    <Grid container alignItems="center" spacing={2}>
                        <Grid item xs={12} md={8}>
                            <Box display="flex" alignItems="center" gap={1.5} mb={0.5}>
                                <Typography fontSize="14px" color="rgba(255,255,255,0.75)">
                                    PPSK Admin Dashboard
                                </Typography>
                                <Tooltip title="Refresh">
                                    <IconButton 
                                        size="small" 
                                        onClick={() => fetchDashboardData(true)}
                                        className={classes.refreshButton}
                                        disabled={refreshing}
                                    >
                                        <RefreshIcon sx={{ fontSize: 18, animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <Typography fontSize={{ xs: "22px", sm: "26px" }} fontWeight={600} color="#fff">
                                પાટીદાર પરિવાર સુરક્ષા કવચ
                            </Typography>
                            <Box display="flex" gap={4} mt={2} flexWrap="wrap">
                                <Box>
                                    <Typography fontSize="12px" color="rgba(255,255,255,0.6)">Total Collection</Typography>
                                    <Typography fontSize="24px" color="#fff" fontWeight={600}>
                                        {formatCurrency(dashboardData.transactions?.totalAmount)}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography fontSize="12px" color="rgba(255,255,255,0.6)">This Month</Typography>
                                    <Typography fontSize="18px" color="#fff" fontWeight={500}>
                                        {formatCurrency(dashboardData.transactions?.thisMonth?.amount)}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography fontSize="12px" color="rgba(255,255,255,0.6)">Today</Typography>
                                    <Typography fontSize="18px" color="#fff" fontWeight={500}>
                                        {formatCurrency(dashboardData.transactions?.today?.amount)}
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={4} className={classes.actionButtons}>
                            <ButtonContained
                                text="Add Member"
                                color="#3E4094"
                                fontWeight={500}
                                backgroundColor="#FFFFFF"
                                padding="10px 20px"
                                fontSize="13px"
                                height="40px"
                                onClick={() => navigate('/admin/addMember')}
                            />
                            <ButtonContained
                                text="Verify Docs"
                                color="#3E4094"
                                fontWeight={500}
                                backgroundColor="#FFFFFF"
                                padding="10px 20px"
                                fontSize="13px"
                                height="40px"
                                onClick={() => navigate('/admin/documents')}
                            />
                            <ButtonContained
                                text="Claims"
                                color="#FFFFFF"
                                fontWeight={500}
                                backgroundColor="rgba(255,255,255,0.15)"
                                padding="10px 20px"
                                fontSize="13px"
                                height="40px"
                                onClick={() => navigate('/admin/claims')}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </Grid>

            {/* Stats Cards */}
            {loading ? (
                <Grid item xs={12} display="flex" justifyContent="center" py={6}>
                    <Box textAlign="center">
                        <CircularProgress sx={{ color: "#3E4094" }} size={40} />
                        <Typography fontSize="13px" color="#6b7280" mt={1.5}>Loading...</Typography>
                    </Box>
                </Grid>
            ) : (
                <>
                    {statsCards.map((stat, index) => (
                        <Grid item xs={6} sm={4} md={2} key={index}>
                            <Box className={classes.statCard} onClick={stat.onClick}>
                                <Box display="flex" alignItems="center" gap={1.5}>
                                    <Box 
                                        className={classes.iconBox} 
                                        sx={{ backgroundColor: stat.iconBg }}
                                    >
                                        {stat.icon}
                                    </Box>
                                    <Box>
                                        <Typography fontSize="12px" color="#6b7280">{stat.title}</Typography>
                                        <Typography fontSize="20px" fontWeight={600} color="#111827">
                                            {stat.isAmount ? stat.count : formatNumber(stat.count)}
                                        </Typography>
                                        <Typography fontSize="11px" color="#9ca3af">{stat.subText}</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Grid>
                    ))}

                    {/* Member Registration Trend Chart */}
                    <Grid item xs={12} md={8}>
                        <Paper elevation={0} className={classes.chartCard}>
                            <Typography className={classes.sectionTitle}>Member Registration Trend</Typography>
                            <ResponsiveContainer width="100%" height={260}>
                                <AreaChart data={dashboardData.memberTrend || []}>
                                    <defs>
                                        <linearGradient id="colorMembers" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#3E4094" stopOpacity={0.2}/>
                                            <stop offset="95%" stopColor="#3E4094" stopOpacity={0}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                                    <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                                    <RechartsTooltip 
                                        contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', fontSize: 12 }}
                                        formatter={(value) => [formatNumber(value), 'Members']}
                                    />
                                    <Area type="monotone" dataKey="count" stroke="#3E4094" strokeWidth={2} fillOpacity={1} fill="url(#colorMembers)" />
                                </AreaChart>
                            </ResponsiveContainer>
                        </Paper>
                    </Grid>

                    {/* Verification Status Pie Chart */}
                    <Grid item xs={12} md={4}>
                        <Paper elevation={0} className={classes.chartCard}>
                            <Typography className={classes.sectionTitle}>Verification Status</Typography>
                            <ResponsiveContainer width="100%" height={260}>
                                <PieChart>
                                    <Pie
                                        data={verificationChartData}
                                        cx="50%"
                                        cy="45%"
                                        innerRadius={50}
                                        outerRadius={80}
                                        paddingAngle={2}
                                        dataKey="count"
                                        nameKey="status"
                                    >
                                        {verificationChartData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <RechartsTooltip 
                                        contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12 }}
                                        formatter={(value, name) => [formatNumber(value), name]}
                                    />
                                    <Legend 
                                        verticalAlign="bottom" 
                                        height={40}
                                        formatter={(value) => <span style={{ color: '#6b7280', fontSize: 11 }}>{value}</span>}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        </Paper>
                    </Grid>

                    {/* Transaction Trend Chart */}
                    <Grid item xs={12} md={8}>
                        <Paper elevation={0} className={classes.chartCard}>
                            <Typography className={classes.sectionTitle}>Transaction Trend</Typography>
                            <ResponsiveContainer width="100%" height={260}>
                                <BarChart data={dashboardData.transactionTrend || []}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                                    <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} tickFormatter={(value) => `₹${(value/1000).toFixed(0)}K`} />
                                    <RechartsTooltip 
                                        contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12 }}
                                        formatter={(value, name) => [name === 'amount' ? formatCurrency(value) : formatNumber(value), name === 'amount' ? 'Amount' : 'Count']}
                                    />
                                    <Bar dataKey="amount" fill="#3E4094" radius={[4, 4, 0, 0]} name="Amount" />
                                </BarChart>
                            </ResponsiveContainer>
                        </Paper>
                    </Grid>

                    {/* Payment Mode Distribution */}
                    <Grid item xs={12} md={4}>
                        <Paper elevation={0} className={classes.chartCard}>
                            <Typography className={classes.sectionTitle}>Payment Methods</Typography>
                            {paymentModeData.length > 0 ? (
                                <ResponsiveContainer width="100%" height={260}>
                                    <PieChart>
                                        <Pie
                                            data={paymentModeData}
                                            cx="50%"
                                            cy="45%"
                                            outerRadius={80}
                                            dataKey="value"
                                            nameKey="name"
                                        >
                                            {paymentModeData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <RechartsTooltip 
                                            contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12 }}
                                            formatter={(value, name, props) => [formatCurrency(props.payload.amount), name]}
                                        />
                                        <Legend 
                                            verticalAlign="bottom" 
                                            height={40}
                                            formatter={(value) => <span style={{ color: '#6b7280', fontSize: 11 }}>{value}</span>}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            ) : (
                                <Box display="flex" alignItems="center" justifyContent="center" height={260}>
                                    <Typography fontSize="13px" color="#9ca3af">No data</Typography>
                                </Box>
                            )}
                        </Paper>
                    </Grid>

                    {/* Recent Transactions */}
                    <Grid item xs={12} md={6}>
                        <Paper elevation={0} className={classes.chartCard}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                                <Typography className={classes.sectionTitle} mb={0}>Recent Transactions</Typography>
                                <Typography 
                                    fontSize="12px" 
                                    color="#3E4094" 
                                    sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                    onClick={() => navigate('/admin/allTransactions')}
                                >
                                    View All
                                </Typography>
                            </Box>
                            <Box sx={{ maxHeight: 280, overflowY: 'auto' }}>
                                {dashboardData.recentTransactions?.length > 0 ? (
                                    dashboardData.recentTransactions.slice(0, 6).map((txn, index) => (
                                        <Box key={index} className={classes.transactionItem}>
                                            <Box>
                                                <Typography fontSize="13px" fontWeight={500} color="#111827">{txn.userName || 'Unknown'}</Typography>
                                                <Typography fontSize="11px" color="#9ca3af">
                                                    {txn.userPhone} • {getPaymentModeName(txn.paymentModeType)}
                                                </Typography>
                                            </Box>
                                            <Box textAlign="right">
                                                <Typography fontSize="13px" fontWeight={600} color="#059669">
                                                    {formatCurrency(txn.amount)}
                                                </Typography>
                                                <Typography fontSize="11px" color="#9ca3af">
                                                    {new Date(txn.createdAt).toLocaleDateString('en-IN')}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ))
                                ) : (
                                    <Box textAlign="center" py={3}>
                                        <Typography fontSize="13px" color="#9ca3af">No transactions</Typography>
                                    </Box>
                                )}
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Quick Stats */}
                    <Grid item xs={12} md={6}>
                        <Paper elevation={0} className={classes.chartCard}>
                            <Typography className={classes.sectionTitle}>Quick Stats</Typography>
                            <Grid container spacing={1.5}>
                                <Grid item xs={6}>
                                    <Box className={classes.miniStatCard}>
                                        <Typography fontSize="11px" color="#6b7280">Login Users</Typography>
                                        <Typography fontSize="20px" fontWeight={600} color="#111827">{formatNumber(dashboardData.members?.loginUsers)}</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6}>
                                    <Box className={classes.miniStatCard}>
                                        <Typography fontSize="11px" color="#6b7280">Re-Uploaded</Typography>
                                        <Typography fontSize="20px" fontWeight={600} color="#111827">{formatNumber(dashboardData.members?.reUploaded)}</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6}>
                                    <Box className={classes.miniStatCard}>
                                        <Typography fontSize="11px" color="#6b7280">Partial Rejected</Typography>
                                        <Typography fontSize="20px" fontWeight={600} color="#111827">{formatNumber(dashboardData.members?.partialRejected)}</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6}>
                                    <Box className={classes.miniStatCard}>
                                        <Typography fontSize="11px" color="#6b7280">Open Tickets</Typography>
                                        <Typography fontSize="20px" fontWeight={600} color="#111827">{formatNumber(dashboardData.tickets?.open)}</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6}>
                                    <Box className={classes.miniStatCard}>
                                        <Typography fontSize="11px" color="#6b7280">Draft Members</Typography>
                                        <Typography fontSize="20px" fontWeight={600} color="#111827">{formatNumber(dashboardData.members?.draft)}</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6}>
                                    <Box className={classes.miniStatCard}>
                                        <Typography fontSize="11px" color="#6b7280">Rejected</Typography>
                                        <Typography fontSize="20px" fontWeight={600} color="#111827">{formatNumber(dashboardData.members?.rejected)}</Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>

                    {/* Quick Actions Section */}
                    <Grid item xs={12}>
                        <Typography className={classes.sectionTitle}>Quick Actions</Typography>
                        <Grid container spacing={1.5}>
                            {[
                                { title: "Members", icon: <PeopleAltOutlinedIcon />, path: '/admin/memberSearch' },
                                { title: "Transactions", icon: <PaymentsOutlinedIcon />, path: '/admin/allTransactions' },
                                { title: "Help Desk", icon: <SupportAgentOutlinedIcon />, path: '/admin/helpDesk' },
                                { title: "Reports", icon: <DescriptionOutlinedIcon />, path: '/admin/reports' },
                                { title: "Reminders", icon: <PendingActionsOutlinedIcon />, path: '/admin/reminders' },
                                { title: "Settings", icon: <PersonAddAltOutlinedIcon />, path: '/admin/settings' },
                            ].map((item, index) => (
                                <Grid item xs={4} sm={2} key={index}>
                                    <Box
                                        onClick={() => navigate(item.path)}
                                        sx={{
                                            p: 2,
                                            borderRadius: "10px",
                                            background: "#fff",
                                            cursor: "pointer",
                                            textAlign: "center",
                                            border: "1px solid #e5e7eb",
                                            transition: "all 0.2s",
                                            "&:hover": {
                                                borderColor: "#3E4094",
                                                background: "#f8f9ff",
                                            }
                                        }}
                                    >
                                        <Box sx={{ color: "#3E4094", mb: 0.5 }}>
                                            {React.cloneElement(item.icon, { sx: { fontSize: 24 } })}
                                        </Box>
                                        <Typography fontSize="12px" fontWeight={500} color="#374151">
                                            {item.title}
                                        </Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                </>
            )}
        </Grid>
    )
}

export default Dashboard
